import React, { useState } from 'react';
import { Save, RotateCcw, CheckCircle2, AlertCircle, Plus, Trash2, FileText, DollarSign } from 'lucide-react';
interface ChargeEntry {
  id: string;
  fiscalYear: string;
  charge: string;
  rateType: string;
  rateName: string;
}
const CATEGORIES = ['Import', 'Export', 'Transit', 'Storage', 'Handling', 'General'];
const FISCAL_YEARS = ['2080/81', '2081/82', '2082/83', '2083/84'];
const RATE_TYPES = ['Per Ton', 'Per CBM', 'Per Day', 'Per Unit', 'Fixed', 'Percentage'];
export function ServiceRegistration() {
  // Main Form State
  const [serviceName, setServiceName] = useState('');
  const [serviceCategory, setServiceCategory] = useState('');
  // Charges List State
  const [charges, setCharges] = useState<ChargeEntry[]>([]);
  // New Charge Form State
  const [newFiscalYear, setNewFiscalYear] = useState('2082/83');
  const [newCharge, setNewCharge] = useState('');
  const [newRateType, setNewRateType] = useState('');
  const [newRateName, setNewRateName] = useState('');
  // UI State
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success'>('idle');
  const handleAddCharge = () => {
    const newErrors: Record<string, string> = {};
    if (!newFiscalYear) newErrors.fiscalYear = 'Required';
    if (!newCharge) newErrors.charge = 'Required';
    if (!newRateType) newErrors.rateType = 'Required';
    if (!newRateName) newErrors.rateName = 'Required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const newChargeEntry: ChargeEntry = {
      id: Math.random().toString(36).substr(2, 9),
      fiscalYear: newFiscalYear,
      charge: newCharge,
      rateType: newRateType,
      rateName: newRateName
    };
    setCharges([...charges, newChargeEntry]);
    // Reset new charge form
    setNewCharge('');
    setNewRateType('');
    setNewRateName('');
    setErrors({});
  };
  const handleRemoveCharge = (id: string) => {
    setCharges(charges.filter(c => c.id !== id));
  };
  const validateMainForm = () => {
    const newErrors: Record<string, string> = {};
    if (!serviceName.trim()) newErrors.serviceName = 'Service Name is required';
    if (!serviceCategory) newErrors.serviceCategory = 'Category is required';
    if (charges.length === 0) newErrors.general = 'Please add at least one charge';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (validateMainForm()) {
      setSaveStatus('success');
      setTimeout(() => {
        setSaveStatus('idle');
        handleReset();
      }, 3000);
    }
  };
  const handleReset = () => {
    setServiceName('');
    setServiceCategory('');
    setCharges([]);
    setNewFiscalYear('2082/83');
    setNewCharge('');
    setNewRateType('');
    setNewRateName('');
    setErrors({});
    setSaveStatus('idle');
  };
  return <div className="flex flex-col gap-6 w-full h-full pb-24 max-w-5xl mx-auto" data-id="element-926">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-927">
        <div data-id="element-928">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-929">
            Service Registration
          </h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-930">
            Create a new service and define its applicable charges.
          </p>
        </div>

        {saveStatus === 'success' && <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-200 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-931">
            <CheckCircle2 size={18} data-id="element-932" />
            <span className="font-medium text-sm" data-id="element-933">
              Service registered successfully!
            </span>
          </div>}
      </div>

      {errors.general && <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 p-3 rounded-lg text-sm font-medium" data-id="element-934">
          <AlertCircle size={18} data-id="element-935" />
          {errors.general}
        </div>}

      {/* Main Service Details */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-936">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100" data-id="element-937">
          <div className="bg-orange-100 p-2 rounded-lg text-orange-600" data-id="element-938">
            <FileText size={20} data-id="element-939" />
          </div>
          <h2 className="text-lg font-bold text-gray-800" data-id="element-940">Basic Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-941">
          <div data-id="element-942">
            <label className="text-xs font-bold text-gray-500 uppercase block mb-2" data-id="element-943">
              Service Name <span className="text-red-500" data-id="element-944">*</span>
            </label>
            <input type="text" value={serviceName} onChange={e => {
            setServiceName(e.target.value);
            if (errors.serviceName) setErrors(prev => ({
              ...prev,
              serviceName: ''
            }));
          }} placeholder="e.g. Container Scanning" className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all ${errors.serviceName ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} data-id="element-945" />
            {errors.serviceName && <span className="text-xs text-red-500 font-medium mt-1 block" data-id="element-946">
                {errors.serviceName}
              </span>}
          </div>

          <div data-id="element-947">
            <label className="text-xs font-bold text-gray-500 uppercase block mb-2" data-id="element-948">
              Category <span className="text-red-500" data-id="element-949">*</span>
            </label>
            <select value={serviceCategory} onChange={e => {
            setServiceCategory(e.target.value);
            if (errors.serviceCategory) setErrors(prev => ({
              ...prev,
              serviceCategory: ''
            }));
          }} className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all bg-white ${errors.serviceCategory ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} data-id="element-950">
              <option value="" data-id="element-951">-- Select Category --</option>
              {CATEGORIES.map(cat => <option key={cat} value={cat} data-id="element-952">
                  {cat}
                </option>)}
            </select>
            {errors.serviceCategory && <span className="text-xs text-red-500 font-medium mt-1 block" data-id="element-953">
                {errors.serviceCategory}
              </span>}
          </div>
        </div>
      </div>

      {/* Charges Section */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-954">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100" data-id="element-955">
          <div className="bg-blue-100 p-2 rounded-lg text-blue-600" data-id="element-956">
            <DollarSign size={20} data-id="element-957" />
          </div>
          <div data-id="element-958">
            <h2 className="text-lg font-bold text-gray-800" data-id="element-959">Service Charges</h2>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-960">
              Define multiple rates and charges for this service
            </p>
          </div>
        </div>

        {/* Add New Charge Form */}
        <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 mb-6" data-id="element-961">
          <h3 className="text-sm font-bold text-gray-700 mb-4" data-id="element-962">
            Add New Charge
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end" data-id="element-963">
            <div data-id="element-964">
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5" data-id="element-965">
                Fiscal Year
              </label>
              <select value={newFiscalYear} onChange={e => setNewFiscalYear(e.target.value)} className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white ${errors.fiscalYear ? 'border-red-300' : 'border-gray-300'}`} data-id="element-966">
                {FISCAL_YEARS.map(fy => <option key={fy} value={fy} data-id="element-967">
                    {fy}
                  </option>)}
              </select>
            </div>

            <div data-id="element-968">
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5" data-id="element-969">
                Charge Amount
              </label>
              <input type="number" value={newCharge} onChange={e => setNewCharge(e.target.value)} placeholder="0.00" className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none ${errors.charge ? 'border-red-300' : 'border-gray-300'}`} data-id="element-970" />
            </div>

            <div data-id="element-971">
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5" data-id="element-972">
                Rate Type
              </label>
              <select value={newRateType} onChange={e => setNewRateType(e.target.value)} className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white ${errors.rateType ? 'border-red-300' : 'border-gray-300'}`} data-id="element-973">
                <option value="" data-id="element-974">Select Type</option>
                {RATE_TYPES.map(rt => <option key={rt} value={rt} data-id="element-975">
                    {rt}
                  </option>)}
              </select>
            </div>

            <div data-id="element-976">
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5" data-id="element-977">
                Rate Name
              </label>
              <input type="text" value={newRateName} onChange={e => setNewRateName(e.target.value)} placeholder="e.g. Standard Rate" className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none ${errors.rateName ? 'border-red-300' : 'border-gray-300'}`} data-id="element-978" />
            </div>
          </div>

          <div className="mt-4 flex justify-end" data-id="element-979">
            <button onClick={handleAddCharge} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-bold transition-colors" data-id="element-980">
              <Plus size={16} data-id="element-981" />
              Add Charge
            </button>
          </div>
        </div>

        {/* Charges Table */}
        {charges.length > 0 ? <div className="overflow-hidden border border-gray-200 rounded-lg" data-id="element-982">
            <table className="w-full text-sm text-left" data-id="element-983">
              <thead className="bg-gray-50 text-gray-700 font-bold uppercase text-xs border-b border-gray-200" data-id="element-984">
                <tr data-id="element-985">
                  <th className="px-4 py-3" data-id="element-986">Fiscal Year</th>
                  <th className="px-4 py-3" data-id="element-987">Rate Name</th>
                  <th className="px-4 py-3" data-id="element-988">Rate Type</th>
                  <th className="px-4 py-3 text-right" data-id="element-989">Charge (NPR)</th>
                  <th className="px-4 py-3 w-16 text-center" data-id="element-990">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-991">
                {charges.map(charge => <tr key={charge.id} className="hover:bg-gray-50 transition-colors" data-id="element-992">
                    <td className="px-4 py-3 font-medium text-gray-900" data-id="element-993">
                      {charge.fiscalYear}
                    </td>
                    <td className="px-4 py-3 text-gray-700" data-id="element-994">
                      {charge.rateName}
                    </td>
                    <td className="px-4 py-3 text-gray-600" data-id="element-995">
                      <span className="bg-gray-100 px-2 py-1 rounded text-xs font-medium border border-gray-200" data-id="element-996">
                        {charge.rateType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900" data-id="element-997">
                      {Number(charge.charge).toLocaleString(undefined, {
                  minimumFractionDigits: 2
                })}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-998">
                      <button onClick={() => handleRemoveCharge(charge.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1" title="Remove Charge" data-id="element-999">
                        <Trash2 size={16} data-id="element-1000" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div> : <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-gray-400" data-id="element-1001">
            <DollarSign size={32} className="mb-2 opacity-50" data-id="element-1002" />
            <p className="text-sm font-medium" data-id="element-1003">No charges added yet.</p>
            <p className="text-xs mt-1" data-id="element-1004">
              Use the form above to add applicable charges.
            </p>
          </div>}
      </div>

      {/* Footer Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10 lg:pl-72" data-id="element-1005">
        <div className="max-w-5xl mx-auto flex justify-between items-center" data-id="element-1006">
          <button onClick={handleReset} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1007">
            <RotateCcw size={18} data-id="element-1008" />
            Reset Form
          </button>

          <button onClick={handleSave} className="flex items-center gap-2 px-8 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5" data-id="element-1009">
            <Save size={18} data-id="element-1010" />
            Save Service
          </button>
        </div>
      </div>
    </div>;
}